import { createContext, useContext } from "react";
import type { FrameworkId } from "../context/FrameworksContext";

// ── GET /nav/counts ───────────────────────────────────────────────────────
// Typed by role (docs/NAVIGATION.md section 3). Consultants and auditors
// viewing a client (?organizationId=) receive the client shape for that org.

/**
 * The next statutory deadline the organisation is running against — the top bar's clock.
 * Mirrors backend/src/lib/statutoryClocks.ts; `now` is the server's clock when the counts were
 * built, so the browser can correct its own skew rather than trust it.
 */
export interface StatutoryClock {
  source: "submission" | "notification";
  id: string;
  label: string;
  detail: string;
  dueAt: string;
  href: string;
  urgency: "overdue" | "due_soon" | "on_time";
  outstanding: number;
  now: string;
}

export interface ClientNavCounts {
  role: "client";
  actions: number;
  unresolvedFeedback: number;
  runningCheck: { id: string; status: string } | null;
  /** null = not scoreable (nist_csf, iwa48, gdpr, uk_gdpr). */
  frameworkScores: Array<{ id: FrameworkId; score: number | null }>;
  publicApiEnabled: boolean;
  /** Operational resilience items needing action now (overdue notifications, lapsed exercises, expiring cover). */
  resilience: number;
  /** Corrective actions overdue, blocked or unowned — the Improvement page's "Needs attention" count. */
  improvement: number;
  /** AI systems needing attention — prohibited practice, unconfirmed detection, review overdue. */
  aiSystems: number;
  /** Regulatory submissions approaching or past a statutory deadline, or submitted with no reference. */
  submissions: number;
  // Assurance uplift (backend routes/nav.routes.ts). Optional so an older API's payload still renders.
  /** Policy approvals, reviews, acknowledgement campaigns and training assignments needing action. */
  governance?: number;
  /** Business impact analyses overdue for review, or whose dependencies cannot recover in time. */
  bia?: number;
  /** Known-exploited vulnerabilities open, and findings past their remediation deadline. */
  vulnerabilities?: number;
  /** Remediation plans awaiting approval, or runs that failed. */
  remediation?: number;
  /** Leavers with offboarding outstanding; joiners with onboarding incomplete. */
  people?: number;
  /** What this member personally has to do: policies to acknowledge, training to complete, approvals to decide. */
  myTasks?: number;
  /** Secrets, special category or card data found; personal data not linked to a processing record. */
  dataDiscovery?: number;
  /** Management system work slipping: audits and reviews overdue, nonconformities without an action, obligations not met. */
  managementSystem?: number;
  /** The next statutory deadline, for the top bar clock. Null when nothing is due inside the horizon. */
  deadline: StatutoryClock | null;
  setup: { sector: boolean; frameworks: boolean; intake: boolean; firstCheck: boolean; evidence: boolean; team: boolean };
  /** Set when the regulated sector is unsettled and this member can settle it (org:admin). */
  sectorPrompt: { resolution: "unset" | "ambiguous"; sector: string | null; sectorCode: string | null } | null;
  /**
   * Rows in each optional register (backend routes/nav.routes.ts RegisterUsage): a module shows
   * in the sidebar once it applies or is in use, and sits under More tools until then.
   * Optional so a counts payload from an older API still renders.
   */
  usage?: {
    incidents: number;
    exercises: number;
    training: number;
    assets: number;
    insurance: number;
    correctiveActions: number;
    networkZones: number;
    aiSystems: number;
    submissions: number;
    suppliers: number;
    businessImpact?: number;
    vulnerabilities?: number;
    quantified?: number;
    playbooks?: number;
    lifecycle?: number;
    assurancePacks?: number;
    policyCampaigns?: number;
    trainingAssignments?: number;
    dataDiscovery?: number;
    // The ISO management-system registers (docs/MANAGEMENT-SYSTEM.md)
    msContext?: number;
    msObjectives?: number;
    msAudits?: number;
    msReviews?: number;
    msCompetence?: number;
    msAspects?: number;
    msHazards?: number;
    msObligations?: number;
    technicalScopes?: number;
    accessReviews?: number;
    policyExceptions?: number;
    changes?: number;
    customerQuestionnaires?: number;
  };
  /** A regime in the organisation's own jurisdictions could designate it an operator of essential services. */
  oesPossible?: boolean;
}

export interface ConsultantNavCounts {
  role: "consultant";
  clientsNeedingAction: number;
  openFindings: number;
  myActions: number;
}

export interface AuditorNavCounts {
  role: "auditor";
  /** GET /auditor/actions total, inside the auditor's live scope. */
  myActions: number;
  clientsPendingReview: number;
}

export type NavCounts = ClientNavCounts | ConsultantNavCounts | AuditorNavCounts;

export interface NavCountsValue {
  counts: NavCounts | null;
  /** Re-fetch now (after an action that changes a count). */
  refresh: () => void;
}

export const NavCountsContext = createContext<NavCountsValue>({ counts: null, refresh: () => {} });

/** The counts the enclosing AppShell polls; null until the first successful load. */
export function useNavCountsContext(): NavCountsValue {
  return useContext(NavCountsContext);
}
